import { fadeIn, fadeOut, hide, isVisible, show } from "./Helpers/animations";
import { initCommonHandlers } from "./Partials/CommonHandlers";
import { initPrintHandlers } from "./Partials/Print";
import { initTranslationHandlers } from "./Partials/Translation";

const searchMenuToggle = document.querySelector('header .toggle-search') as HTMLButtonElement | null;
const mobileSideMenuToggle = document.querySelector('header .toggle-side-menu') as HTMLDivElement | null;
const dropdownContainer = document.querySelector('.dropdowns') as HTMLDivElement | null;
const dropdowns = document.querySelectorAll('.dropdowns .dropdown') as NodeListOf<HTMLDivElement>;
const mobileSearch = document.getElementById('dropdown-search') as HTMLElement | null;
const sideMenuWrap = document.querySelector('header .side-menu-wrap') as HTMLElement | null;

const initMenuToggles = () => {
    mobileSideMenuToggle?.addEventListener('click', () => {
        if (mobileSideMenuToggle.classList.contains('opened')) {
            mobileSideMenuToggle.classList.remove('opened');
            mobileSideMenuToggle.ariaExpanded = 'false';

            fadeOut(sideMenuWrap, 200, {
                callback: () => {
                    document.documentElement.classList.remove('behind');
                    mobileSideMenuToggle.focus();
                }
            });
            fadeOut(dropdownContainer, 200, { removeClasses: ['opened'] });
        } else {
            mobileSideMenuToggle.classList.add('opened');
            mobileSideMenuToggle.ariaExpanded = 'true';

            document.documentElement.classList.add('behind');

            if (searchMenuToggle?.classList.contains('opened')) {
                searchMenuToggle.classList.remove('opened');
                searchMenuToggle.ariaExpanded = 'false';
                
                fadeOut(mobileSearch, 200, {
                    callback: () => fadeIn(sideMenuWrap, 200, { 
                        callback: () => {
                            hide(dropdownContainer, { removeClasses: ['opened'] });
                            sideMenuWrap?.querySelector('a')?.focus() 
                        }
                    })
                });
            } else {
                fadeIn(sideMenuWrap, 200, { callback: () => sideMenuWrap?.querySelector('a')?.focus() });
            }
        }
    });

    searchMenuToggle?.addEventListener('click', () => {
        if (searchMenuToggle.classList.contains('opened')) {
            searchMenuToggle.classList.remove('opened');
            searchMenuToggle.ariaExpanded = 'false';

            fadeOut(mobileSearch, 200, {
                callback: () => {
                    document.documentElement.classList.remove('behind');
                    searchMenuToggle.focus();
                }
            });
            fadeOut(dropdownContainer, 200, { removeClasses: ['opened'] });
        } else {
            searchMenuToggle.classList.add('opened');
            searchMenuToggle.ariaExpanded = 'true';

            document.documentElement.classList.add('behind');

            if (mobileSideMenuToggle?.classList.contains('opened')) {
                mobileSideMenuToggle.classList.remove('opened');
                mobileSideMenuToggle.ariaExpanded = 'false';

                show(dropdownContainer, { addClasses: ['opened'] });

                fadeOut(sideMenuWrap, 200, {
                    callback: () => fadeIn(mobileSearch, 200, { callback: () => document.getElementById('search-terms-mobile')?.focus() })
                });
            } else {
                dropdowns.forEach(dropdown => hide(dropdown));
                fadeIn(dropdownContainer, 200, { addClasses: ['opened'] });
                fadeIn(mobileSearch, 200, { callback: () => document.getElementById('search-terms-mobile')?.focus() });
            }
        }
    });
};

const resetMobileMenus = () => {
    document.documentElement.classList.remove('behind');
    document.body.classList.remove('behind');
    
    if (mobileSideMenuToggle) {
        mobileSideMenuToggle.classList.remove('opened');
        mobileSideMenuToggle.ariaExpanded = 'false';
    }

    if (searchMenuToggle) {
        searchMenuToggle.classList.remove('opened');
        searchMenuToggle.ariaExpanded = 'false';
    }

    hide(sideMenuWrap);
    dropdowns.forEach(dropdown => hide(dropdown));
    hide(dropdownContainer, { removeClasses: ['opened'] });
};

const initMobileMenuResetOnResizeHandler = () => {
    window.addEventListener('resize', () => window.innerWidth > 991 && resetMobileMenus());
};

const initTopNavDisplayOnFocus = (topNavLinks: NodeListOf<HTMLAnchorElement>) => {
    topNavLinks.forEach(topNavLink => 
        topNavLink.addEventListener('focus', () => {
            if (topNavLink.nextElementSibling && !topNavLink.nextElementSibling.classList.contains('nav-visible')) {
                topNavLink.nextElementSibling.classList.add('nav-visible');
            }
        })
    );
};

const initTopNavHideOnBlur = (topNavLinks: NodeListOf<HTMLAnchorElement>) => {
    topNavLinks.forEach(topNavLink => 
        topNavLink.addEventListener('blur', event => {
            const currentNavLinks = topNavLink.parentElement?.querySelectorAll('a');
            if (topNavLink.nextElementSibling && currentNavLinks && ![...currentNavLinks].includes(event.relatedTarget as HTMLAnchorElement)) {
                topNavLink.nextElementSibling.classList.remove('nav-visible');
            }
        })
    );
};

const initTopNavKeyboardNavigation = (topNavLinks: NodeListOf<HTMLAnchorElement>) => {
    topNavLinks.forEach(topNavLink => {
        topNavLink.addEventListener('keydown', event => {
            const currentNavLinks = topNavLink.parentElement?.querySelectorAll('a');
            if (currentNavLinks) {
                const currentMainNavIndex = [...topNavLinks].indexOf(topNavLink);

                switch (event.key) {
                    case 'ArrowUp':
                        event.preventDefault();
                        if (currentNavLinks.length > 1) {
                            if (topNavLink.nextElementSibling?.classList.contains('nav-visible')) {
                                topNavLink.nextElementSibling.classList.add('nav-visible');
                            }
                            currentNavLinks[currentNavLinks.length - 1].focus();
                        }
                        break;
                    case 'ArrowDown':
                        event.preventDefault();
                        if (currentNavLinks.length > 1) {
                            if (topNavLink.nextElementSibling?.classList.contains('nav-visible')) {
                                topNavLink.nextElementSibling.classList.add('nav-visible');
                            }
                            currentNavLinks[1].focus();
                        }
                        break;
                    case 'ArrowLeft':
                        event.preventDefault();
                        if (topNavLinks[currentMainNavIndex - 1]) {
                            topNavLinks[currentMainNavIndex - 1].focus();
                        } else if (topNavLinks[topNavLinks.length - 1]) {
                            topNavLinks[topNavLinks.length - 1].focus();
                        }
                        break;
                    case 'ArrowRight':
                        event.preventDefault();
                        if (topNavLinks[currentMainNavIndex + 1]) {
                            topNavLinks[currentMainNavIndex + 1].focus();
                        } else if (topNavLinks[0]) {
                            topNavLinks[0].focus();
                        }
                        break;
                    case 'Home':
                        event.preventDefault();
                        if (topNavLinks[0]) {
                            topNavLinks[0].focus();
                        }
                        break;
                    case 'End':
                        event.preventDefault();
                        if (topNavLinks[topNavLinks.length - 1]) {
                            topNavLinks[topNavLinks.length - 1].focus();
                        }
                        break;
                    case 'Escape':
                        if (topNavLink.nextElementSibling) {
                            topNavLink.nextElementSibling.classList.remove('nav-visible');
                        }
                        break;
                    default:
                        break;
                }
            }
        });

        if (topNavLink.nextElementSibling) {
            topNavLink.nextElementSibling.querySelectorAll('a').forEach(childLink => {
                childLink.addEventListener('blur', event => 
                    document.querySelectorAll('.header-nav .links > li').forEach(headerMenu => {
                        console.log(`Contains Target: ${headerMenu.contains(childLink)}`);
                        console.log(`Does Not Contain RelatedTarget: ${!headerMenu.contains(event.relatedTarget as HTMLElement)}`);

                        if (event.target && event.relatedTarget && 
                            headerMenu.contains(childLink) && !headerMenu.contains(event.relatedTarget as HTMLElement)) {
                                topNavLink.nextElementSibling?.classList.remove('nav-visible');
                        }
                    })
                );

                childLink.addEventListener('keydown', event => {
                    if (topNavLink.parentElement) {
                        const currentNavLinks = topNavLink.parentElement?.querySelectorAll('a') as NodeListOf<HTMLAnchorElement>;
                        const currentFocusIndex = [...currentNavLinks].indexOf(childLink);
                        const currentMainNavIndex = [...topNavLinks].indexOf(topNavLink);

                        switch (event.key) {
                            case 'ArrowUp':
                                event.preventDefault();
                                if (currentNavLinks[currentFocusIndex - 1]) {
                                    currentNavLinks[currentFocusIndex - 1].focus();
                                } else if (currentNavLinks[currentNavLinks.length - 1]) {
                                    currentNavLinks[currentNavLinks.length - 1].focus();
                                }
                                break;
                            case 'ArrowDown':
                                event.preventDefault();
                                if (currentNavLinks[currentFocusIndex + 1]) {
                                    currentNavLinks[currentFocusIndex + 1].focus();
                                } else if (currentNavLinks[0]) {
                                    currentNavLinks[0].focus();
                                }
                                break;
                            case 'ArrowLeft':
                                if (currentMainNavIndex !== undefined && currentMainNavIndex !== -1 && topNavLinks[currentMainNavIndex - 1]) {
                                    topNavLinks[currentMainNavIndex - 1].focus();
                                } else if (topNavLinks[topNavLinks.length - 1]) {
                                    topNavLinks[topNavLinks.length - 1].focus();
                                }
                                break;
                            case 'ArrowRight':
                                if (currentMainNavIndex !== undefined && currentMainNavIndex !== -1 && topNavLinks[currentMainNavIndex + 1]) {
                                    topNavLinks[currentMainNavIndex + 1].focus();
                                } else if (topNavLinks[0]) {
                                    topNavLinks[0].focus();
                                }
                                break;
                            case 'Home':
                                event.preventDefault();
                                if (currentNavLinks[1]) {
                                    currentNavLinks[1].focus();
                                }
                                break;
                            case 'End':
                                event.preventDefault();
                                if (currentNavLinks[currentNavLinks.length - 1]) {
                                    currentNavLinks[currentNavLinks.length - 1].focus();
                                }
                                break;
                            case 'Escape':
                                event.preventDefault();
                                if (currentNavLinks[0]) {
                                    currentNavLinks[0].focus();
                                    setTimeout(() => topNavLink.parentElement?.classList.remove('nav-visible'), 0);
                                }
                                break;
                            default: 
                                break;
                        }
                    }
                });
            });
        }
    });
};

const fadeOutMobileMenus = (focusElement: HTMLElement | null = null) => {
    document.documentElement.classList.remove('behind');
    document.body.classList.remove('behind');
    
    if (mobileSideMenuToggle) {
        mobileSideMenuToggle.classList.remove('opened');
        mobileSideMenuToggle.ariaExpanded = 'false';
    }

    if (searchMenuToggle) {
        searchMenuToggle.classList.remove('opened');
        searchMenuToggle.ariaExpanded = 'false';
    }

    fadeOut(sideMenuWrap, 200);
    dropdowns.forEach(dropdown => fadeOut(dropdown, 200));
    fadeOut(dropdownContainer, 200, { 
        callback: () => {
            dropdownContainer?.classList.remove('opened');
            focusElement?.focus();
        }
    });
};

const initDropdownToggleHandlers = () => {
    // Desktop Toggles
    const desktopDropdownsToggles = document.querySelectorAll('header .link-site button') as NodeListOf<HTMLButtonElement>;
    desktopDropdownsToggles.forEach(button => button.addEventListener('click', () => {
        button.ariaExpanded = 'true';
        document.body.classList.add('behind');
        hide(mobileSearch);
        show(dropdownContainer, { addClasses: ['opened'] });

        const controlledDropdown = button.ariaControlsElements?.[0] as HTMLElement | undefined;
        fadeIn(controlledDropdown, 300, { callback: () => (controlledDropdown?.querySelector('.close-dropdowns') as HTMLAnchorElement)?.focus() });
    }));

    // Close Toggles
    document.querySelectorAll('.dropdowns .close-dropdowns').forEach(button => button.addEventListener('click', () => {
        desktopDropdownsToggles.forEach(toggle => toggle.ariaExpanded = 'false');
        fadeOutMobileMenus();
    }));
};

const initDropdownFocusTraps = () => {
    dropdowns.forEach(dropdown => {
        (dropdown.querySelector('.close-dropdowns') as HTMLAnchorElement)?.addEventListener('keydown', event => {
            if (event.key === 'Tab' && event.shiftKey) {
                event.preventDefault();
                const targets = document.querySelectorAll('a, button, input') as NodeListOf<HTMLAnchorElement | HTMLButtonElement | HTMLInputElement>;
                [...targets].findLast(target => isVisible(target))?.focus();
            }
        });

        dropdown.addEventListener('focusout', event => {
            if (!event.relatedTarget || !dropdown.contains(event.relatedTarget as HTMLElement)) {
                (dropdown.querySelector('.close-dropdowns') as HTMLAnchorElement | null)?.focus();
            }
        });

        dropdown.addEventListener('keydown', event => {
            const targets = document.querySelectorAll('a, button, input') as NodeListOf<HTMLAnchorElement | HTMLButtonElement | HTMLInputElement>;
            const lastVisible = [...targets].findLast(target => isVisible(target));
            
            if (event.key === 'Tab' && !event.shiftKey && event.target === lastVisible) {
                event.preventDefault();
                targets[0]?.focus();
            } else if (event.key == 'Escape') {
                event.preventDefault();
                fadeOutMobileMenus(document.querySelector(`button[aria-controls=${dropdown.id}`) as HTMLButtonElement | null);
            }
        })
    });

    mobileSideMenuToggle?.addEventListener('keydown', event => {
        if (event.key === 'Tab' && event.shiftKey) {
            if (isVisible(dropdownContainer)) {
                event.preventDefault();
                const currentTargets = dropdownContainer?.querySelectorAll('a, button, input') as NodeListOf<HTMLAnchorElement | HTMLButtonElement | HTMLInputElement>;
                [...currentTargets].findLast(target => isVisible(target))?.focus();
            } else if (isVisible(sideMenuWrap?.querySelector('.menu-lists'))) {
                event.preventDefault();
                const currentTargets = sideMenuWrap?.querySelectorAll('a, button, input') as NodeListOf<HTMLAnchorElement | HTMLButtonElement | HTMLInputElement>;
                [...currentTargets].findLast(target => isVisible(target))?.focus();
            }
        } else if (event.key === 'Escape' && (isVisible(dropdownContainer) || isVisible(sideMenuWrap?.querySelector('.menu-lists')))) {
            fadeOutMobileMenus();
        }
    });

    searchMenuToggle?.addEventListener('keydown', event => {
        if (event.key === 'Tab' && !event.shiftKey) {
            if (isVisible(dropdownContainer)) {
                event.preventDefault();
                const currentTargets = dropdownContainer?.querySelectorAll('a, button, input') as NodeListOf<HTMLAnchorElement | HTMLButtonElement | HTMLInputElement>;
                [...currentTargets].find(target => isVisible(target))?.focus();
            } else if (isVisible(sideMenuWrap?.querySelector('.menu-lists'))) {
                event.preventDefault();
                const currentTargets = sideMenuWrap?.querySelectorAll('a, button, input') as NodeListOf<HTMLAnchorElement | HTMLButtonElement | HTMLInputElement>;
                [...currentTargets].find(target => isVisible(target))?.focus();
            }
        } else if (event.key === 'Escape' && (isVisible(dropdownContainer) || isVisible(sideMenuWrap?.querySelector('.menu-lists')))) {
            fadeOutMobileMenus();
        }
    });

    sideMenuWrap?.addEventListener('keydown', event => {
        if (mobileSideMenuToggle?.classList.contains('opened')) {
            if (event.key === 'Tab' && !event.shiftKey) {
                const currentTargets = sideMenuWrap.querySelectorAll('a, button, input') as NodeListOf<HTMLAnchorElement | HTMLButtonElement | HTMLInputElement>;
                const lastVisible = [...currentTargets].findLast(target => isVisible(target));

                if (event.target === lastVisible) {
                    event.preventDefault();
                    mobileSideMenuToggle.focus();
                }
            } else if (event.key === 'Escape') {
                fadeOutMobileMenus(mobileSideMenuToggle);
            }
        }
    });

    mobileSearch?.addEventListener('keydown', event => {
        if (isVisible(mobileSearch)) {
            if (event.key === 'Tab') {
                const currentTargets = mobileSearch.querySelectorAll('a, button, input') as NodeListOf<HTMLAnchorElement | HTMLButtonElement | HTMLInputElement>;
                const lastVisible = [...currentTargets].findLast(target => isVisible(target));
                const firstVisible = [...currentTargets].find(target => isVisible(target));

                if (event.shiftKey && event.target === firstVisible) {
                    event.preventDefault();
                    searchMenuToggle?.focus();
                } else if (!event.shiftKey && event.target === lastVisible) {
                    event.preventDefault();
                    mobileSideMenuToggle?.focus();
                }
            } else if (event.key === 'Escape') {
                fadeOutMobileMenus(searchMenuToggle);
            }
        }
    });
};

const initTopNavigationHandlers = () => {
    const topNavLinks = document.querySelectorAll(".header-nav .links > li > a") as NodeListOf<HTMLAnchorElement>;
    initTopNavDisplayOnFocus(topNavLinks);
    initTopNavHideOnBlur(topNavLinks);
    initTopNavKeyboardNavigation(topNavLinks);   
};

const initMobileKeyboardNavigationHandlers = () => {
    
};

initMenuToggles();
initMobileMenuResetOnResizeHandler();
initDropdownToggleHandlers();
initDropdownFocusTraps();
initTopNavigationHandlers();
initCommonHandlers();
initTranslationHandlers();
initPrintHandlers();
initMobileKeyboardNavigationHandlers();